import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Input, Select } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { UrlFilterChip } from '../../components/ui/UrlFilterChip.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useUrlFilter } from '../../hooks/useUrlFilter.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { type PhotoWindowState, type PhotoWindows, STATE_TONE, mytWhen } from './photoWindows.js';

/** 052: resolved vehicle summary. Null when the car record is gone. */
interface BookingCar {
  id: string;
  /** Composed brand + model — never the `·` specification dump. */
  label: string;
  plate_number: string;
  image_url: string | null;
}

/**
 * #17: resolved customer summary. Null when the account is gone, and absent entirely on a
 * payload from a backend that predates the field — administrator-only either way.
 */
interface BookingCustomer {
  id: string;
  /** The account's name, or its email when it carries no name. */
  name: string;
  email: string;
  /** #24. Null when the account has none. The list shows the name only. */
  phone: string | null;
}

interface Booking {
  /** 053: canonical identifier. `_id` is still on the wire but deprecated. */
  id: string;
  user_id: string;
  car_id: string;
  car: BookingCar | null;
  customer?: BookingCustomer | null;
  status: string;
  pickup_at: string;
  return_at: string;
  total_price_minor: number;
  currency: string;
  created_at: string;
  /** 325 (#477): the rental this booking heads. The row reads as the rental when present. */
  rental?: {
    return_at: string;
    total_price_minor: number;
    extension_count: number;
    status: string;
  };
  /** 374: the rental's customer photo windows; absent while customer photos are off. */
  photo_windows?: PhotoWindows;
}

interface BookingsResponse {
  items: Booking[];
  total: number;
  page: number;
  pages: number;
}

const STATUSES = [
  'awaiting_payment',
  'pending',
  'confirmed',
  'checked_out',
  'checked_in',
  'completed',
  'cancelled',
  'expired',
];

function formatMyr(minor: number) {
  return `RM ${(minor / 100).toFixed(2)}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * 323 (#627): pickup and return with their TIME, so a rental and its extension, or two rentals of one
 * car on one day, can be told apart without opening them. Always Malaysia time (FR-008): the rental
 * happens there, and a fixed zone means an 11:30 pm pickup never shows as the next day's date on a
 * browser set to another zone.
 */
function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  });
}

/** 374: the list's short tag for a moment's state: P/R with a mark. */
const TAG_MARK: Record<PhotoWindowState, string> = {
  sent: '✓',
  open: '⏱',
  missed: '!',
  later: '–',
  unavailable: '–',
};

const TAG_WORDS: Record<PhotoWindowState, string> = {
  sent: 'sent',
  open: 'open now',
  missed: 'not sent',
  later: 'not yet',
  unavailable: 'not available',
};

/** 374 (US6): the rental's pickup (P) and return (R) photos, each a link to the booking's Car condition card. */
function PhotoTags({ id, windows }: { id: string; windows?: PhotoWindows }) {
  if (!windows) return <span className="text-steel">—</span>;
  return (
    <span className="inline-flex gap-1">
      {(['pickup', 'return'] as const).map((m) => {
        const w = windows[m];
        const damage = m === 'return' && w.state === 'sent' && (w.damage_count ?? 0) > 0;
        const word = m === 'pickup' ? 'Pickup' : 'Return';
        const when =
          w.state === 'sent' && w.sent_at
            ? `, ${mytWhen(w.sent_at)}`
            : w.state === 'open'
              ? `, until ${mytWhen(w.closes_at)}`
              : w.state === 'later'
                ? `, from ${mytWhen(w.opens_at)}`
                : '';
        const title = `${word} photos ${TAG_WORDS[w.state]}${when}${damage ? ', damage reported' : ''}`;
        return (
          <Link
            key={m}
            to={`/bookings/${id}#car-condition`}
            title={title}
            aria-label={title}
            data-testid={`photos-${m}`}
            className={`whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-bold ${
              damage ? 'bg-semantic-danger-bg text-semantic-danger-text' : STATE_TONE[w.state]
            }`}
          >
            {m === 'pickup' ? 'P' : 'R'} {TAG_MARK[w.state]}
            {damage ? ' ⚠' : ''}
          </Link>
        );
      })}
    </span>
  );
}

const PHOTOS_COLUMN: Column<Booking> = {
  key: 'photo_windows',
  header: 'Photos',
  render: (row) => <PhotoTags id={row.id} windows={row.photo_windows} />,
};

const columns: Column<Booking>[] = [
  {
    key: 'id',
    header: 'ID',
    render: (row) => (
      <div className="flex flex-col gap-0.5">
        <Link to={`/bookings/${row.id}`} className="font-mono text-xs text-primary hover:underline">
          {row.id.slice(-8)}
        </Link>
        {/* 325: an original booking with extensions is one rental; its legs are on its page. */}
        {row.rental && row.rental.extension_count > 0 && (
          <span data-testid="booking-extended" className="text-xs text-steel">
            Extended ×{row.rental.extension_count}
          </span>
        )}
      </div>
    ),
  },
  {
    // #17: who made the booking, and the way through to their account.
    key: 'customer',
    header: 'Customer',
    render: (row) =>
      row.customer ? (
        <Link to={`/users/${row.customer.id}`} className="text-primary hover:underline">
          {row.customer.name}
        </Link>
      ) : (
        <span className="text-steel">—</span>
      ),
  },
  {
    // 052: staff previously identified rows by _id.slice(-8) alone.
    key: 'car',
    header: 'Car',
    render: (row) =>
      row.car ? (
        <div className="min-w-0">
          <div className="truncate text-ink">{row.car.label}</div>
          <div className="font-mono text-xs text-steel">{row.car.plate_number}</div>
        </div>
      ) : (
        <span className="text-steel">—</span>
      ),
  },
  { key: 'pickup_at', header: 'Pickup', render: (row) => formatDateTime(row.pickup_at) },
  {
    key: 'return_at',
    header: 'Return',
    render: (row) => formatDateTime(row.rental?.return_at ?? row.return_at),
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => <StatusBadge status={row.rental?.status ?? row.status} />,
  },
  {
    key: 'total_price_minor',
    header: 'Total',
    render: (row) => formatMyr(row.rental?.total_price_minor ?? row.total_price_minor),
  },
  { key: 'created_at', header: 'Created', render: (row) => formatDate(row.created_at) },
];

/** 355: the dashboard's Today buckets this list can be opened on, with the name the chip shows. */
const BUCKET_LABELS = {
  pickups_today: 'Pickups today',
  returns_today: 'Returns today',
  overdue: 'Overdue returns',
  payment_mismatch: 'Paid, waiting for staff',
  unpaid_holding: 'Unpaid, holding a slot',
  // 416 (#1431)
  outside_zone: 'Outside zone',
} as const;
const BUCKETS = Object.keys(BUCKET_LABELS) as Array<keyof typeof BUCKET_LABELS>;

export function BookingListPage() {
  const ability = useAbility();
  // 202 (#287): same ability the admin-create route itself checks.
  const canCreate = ability.can('manage', 'Booking');
  const [page, setPage] = useState(1);
  // 355 (FR-011): opened from a dashboard count, the list starts on that count's rows.
  const [bucket, clearBucket] = useUrlFilter('bucket', BUCKETS);
  const [status, setStatus] = useState('');
  const [pickupFrom, setPickupFrom] = useState('');
  const [pickupTo, setPickupTo] = useState('');
  /**
   * 158 (#4): find the bookings for a car by its plate. This list already showed the plate on
   * every row, so it named the one thing it could not be filtered by.
   *
   * Two pieces of state and a local debounce, exactly as the fleet list does — the platform
   * resolves plate → car ids per request, so a read per keystroke costs a lookup each time.
   */
  const [plateInput, setPlateInput] = useState('');
  const [plateQuery, setPlateQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setPlateQuery(plateInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [plateInput]);

  /**
   * 323 (#626): "whose booking is this" — a customer's name, email or phone, or a booking reference,
   * resolved by the platform (`GET /bookings?q=`). Same debounce and page reset as the plate box. The
   * platform matches customer details only for `manage Booking` (FR-007), so the label promises only
   * what the caller can actually search.
   */
  const canSearchCustomers = ability.can('manage', 'Booking');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const params = new URLSearchParams({ page: String(page), limit: '20' });
  if (status) params.set('status', status);
  if (bucket) params.set('bucket', bucket);
  if (pickupFrom) params.set('pickup_from', pickupFrom);
  if (pickupTo) params.set('pickup_to', pickupTo);
  // Composed with the filters above, never in place of them (FR-006).
  if (plateQuery) params.set('plate_number', plateQuery);
  if (searchQuery) params.set('q', searchQuery);

  const { data, isLoading, error } = useQuery<BookingsResponse>({
    queryKey: [
      ...RESOURCE_KEYS.booking.list,
      page,
      bucket,
      status,
      pickupFrom,
      pickupTo,
      plateQuery,
      searchQuery,
    ],
    queryFn: () => api.get(`/bookings?${params.toString()}`),
    // 063 (FR-010): a refusal is not a transient fault; retrying it only keeps
    // the caller reading the empty state.
    retry: false,
  });

  const items = data?.items ?? [];
  // 374: the Photos column, after Status, only while the platform publishes windows (customer photos on).
  const shownColumns = items.some((b) => b.photo_windows)
    ? [
        ...columns.slice(0, columns.findIndex((c) => c.key === 'status') + 1),
        PHOTOS_COLUMN,
        ...columns.slice(columns.findIndex((c) => c.key === 'status') + 1),
      ]
    : columns;
  const totalPages = data?.pages ?? 1;

  return (
    <div>
      <PageHeader
        title="Bookings"
        actions={
          canCreate ? (
            <Link to="/bookings/new">
              <Button size="sm">Create booking</Button>
            </Link>
          ) : undefined
        }
      />
      {bucket ? (
        <UrlFilterChip
          label={BUCKET_LABELS[bucket]}
          onClear={() => {
            clearBucket();
            setPage(1);
          }}
        />
      ) : null}
      <div className="mb-4 flex flex-wrap gap-3">
        {/* 158 (#4): first in the row — a plate is how staff refer to a car. */}
        <Input
          type="search"
          data-testid="plate-filter"
          aria-label="Filter by plate number"
          placeholder="Filter by plate number…"
          className="w-full sm:w-56"
          value={plateInput}
          onChange={(e) => setPlateInput((e.target as HTMLInputElement).value)}
        />
        <Input
          type="search"
          data-testid="booking-search"
          aria-label={
            canSearchCustomers ? 'Search customer or booking' : 'Search booking reference'
          }
          placeholder={
            canSearchCustomers ? 'Name, email, phone or booking reference…' : 'Booking reference…'
          }
          className="w-full sm:w-72"
          value={searchInput}
          onChange={(e) => setSearchInput((e.target as HTMLInputElement).value)}
        />
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </option>
          ))}
        </Select>

        <div className="flex flex-wrap items-center gap-2 text-sm text-steel">
          <span>Pickup from</span>
          <Input
            type="date"
            value={pickupFrom}
            onChange={(e) => {
              setPickupFrom(e.target.value);
              setPage(1);
            }}
          />
          <span>to</span>
          <Input
            type="date"
            value={pickupTo}
            onChange={(e) => {
              setPickupTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>
      <DataTable
        columns={shownColumns}
        data={items}
        loading={isLoading}
        keyExtractor={(r) => r.id}
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: totalPages,
          onPageChange: setPage,
        }}
      />{' '}
    </div>
  );
}
